/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import css from '@emotion/css/macro'
import { CalendarOutlined } from '@ant-design/icons'
import MapMarkerIcon from 'src/components/icons/MapMarkerIcon'
import { Link } from 'react-router-dom'
import { useDragAwareClick } from 'src/hooks/useDragAwareClick'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { font, weightSemiBold } from 'src/components/css'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { getEventCategory } from '@sportninja/common/features/event/eventCategories'
import { resolveEventCompetition } from '@sportninja/common/features/event/eventOwnership'
import { formatEventLocation } from '@sportninja/common/features/event/formatEventLocation'
import { resolveEventTimezone } from '@sportninja/common/features/event/resolveEventTimezone'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { formatEventTimes } from '@sportninja/common/features/event/formatEventTimes'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'

/* Same asset the list row falls back to, so a team with no crest matches. */
const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface UpcomingEventCardProps {
  event: SnEvent
}

/**
 * One event in the home sidebar's Upcoming Events widget.
 *
 * A separate component from `EventListRow` on purpose. That row is built for
 * the schedule — full width, a centred title band, a calendar watermark and a
 * centred date block — and does not compress to the sidebar's 336px. Bending
 * it to would change how every schedule row looks.
 *
 * The anatomy here mirrors mobile's `EventCard` instead: two columns, the left
 * running team → title → competition → location, the right holding the
 * category badge above a vertically-centred date. So a user who checks the app
 * on their phone and their laptop reads the same object twice.
 *
 * Still not a fork — the category resolver, the competition resolver, the time
 * formatting and the asset resolver are all the shared ones.
 */
const UpcomingEventCard: React.FC<UpcomingEventCardProps> = ({ event }) => {
  const category = getEventCategory(event.category)
  const isCancelled = Boolean(event.cancelled)
  const competition = resolveEventCompetition(event)
  const teamName = event.team?.name_full || event.team?.name
  const hasTeam = Boolean(teamName)
  // Never the raw `full_path`: resolveAssetUrl rewrites the S3 host to the CDN.
  const teamLogo = resolveAssetUrl(event.team?.image)
  // The SHARED resolver rather than a hand-rolled fallback: it also covers the
  // facility ("{venue} - {facility}") and the street address, and returns ''
  // when the event has no location at all so the row hides.
  const locationName = formatEventLocation(event)
  // Trimmed so a whitespace-only value does not open an empty row.
  const description = event.details?.trim() || ''

  // The SAME shared formatter the mobile card uses, rather than the schedule
  // row's `useEventRowTimes` — that hook returns one combined
  // 'MONTH DD, YYYY - H:MM AM (ZZZZ)' string, which cannot be split into the
  // date/time/zone stack this card shows. Going to `formatEventTimes` directly
  // keeps the two platforms rendering the same three lines from one source.
  //
  // `eventZone` falls back through venue/address, so an event authored
  // elsewhere never renders silently on the viewer's clock.
  const { start } = formatEventTimes({
    startsAt: event.starts_at,
    // The card shows the START only — no end time, matching mobile.
    endsAt: null,
    eventZone: resolveEventTimezone(event),
    viewerZone: getBrowserTimezone(),
  })

  /* Releasing a carousel drag over the card must settle the slide, not open
     the event — see the hook. */
  const dragAware = useDragAwareClick()

  return (
    <Link
      to={`${ROUTES.EVENT_ROOT}/${event.id}`}
      css={cardStyles}
      {...dragAware}
      /* An anchor is natively draggable, and that drag swallows the mousemove
         stream react-slick tracks a swipe with — so without this the carousel
         cannot be dragged from the card, only from its arrows. The sibling
         game carousel never hit this because its cards navigate on click
         rather than being links. Click-through is unaffected. */
      draggable={false}
    >
      <div css={columnsStyles}>
        <div css={leftColumnStyles}>
          {hasTeam ? (
            <span css={teamRowStyles}>
              <img
                src={teamLogo || TEAM_PLACEHOLDER}
                alt=''
                aria-hidden='true'
                css={teamLogoStyles}
                onError={(e) => {
                  e.currentTarget.src = TEAM_PLACEHOLDER
                }}
              />
              <span css={[teamNameStyles, isCancelled && mutedStyles]}>
                {teamName}
              </span>
            </span>
          ) : null}
          {/* An icon marks the type rather than a written "Event:" label — the
              same treatment the mobile widget card uses, and on a 336px column
              it is width the event's own name gets back. */}
          <span css={titleRowStyles}>
            <CalendarOutlined
              css={[typeIconStyles, isCancelled && typeIconMutedStyles]}
              aria-hidden='true'
            />
            <span css={[titleStyles, isCancelled && titleCancelledStyles]}>
              {capitalizeWords(event.name ?? '')}
            </span>
          </span>
          {competition ? (
            <span css={competitionStyles}>{competition.name}</span>
          ) : null}
          {/* Between the competition and the location, the same slot the
              mobile card gives it. Clamped to two lines so a long description
              cannot run the card away from its neighbours. */}
          {description ? (
            <span css={descriptionStyles}>{description}</span>
          ) : null}
          {/* Last in the column and pushed to its bottom, so the venue closes
              the card at a consistent y however tall the rest grows. */}
          {locationName ? (
            <span css={locationStyles}>
              <MapMarkerIcon
                size={14}
                color={colors.PRIMARY}
                css={pinStyles}
                aria-hidden='true'
              />
              <span css={locationTextStyles}>{locationName}</span>
            </span>
          ) : null}
        </div>
        <div css={rightColumnStyles}>
          {/* Rendered even when empty, so a card with no category still
              reserves the badge's height and its date starts from the same y
              as its neighbours'. */}
          <span css={tagSlotStyles}>
            {isCancelled ? (
              <span css={cancelledTagStyles}>Cancelled</span>
            ) : category ? (
              <span
                css={categoryTagStyles}
                style={{
                  backgroundColor: category.color,
                  color: category.textColor,
                }}
              >
                {category.label}
              </span>
            ) : null}
          </span>
          {/* Date over time, matching mobile: 'Thu, Sep 3' then '2:24 PM PDT'.
              The viewer's own zone leads — that is the clock they plan
              around — and the event's own zone follows, muted, only when the
              two render this instant differently. */}
          <span css={timeGroupStyles}>
            {start.weekdayShortDate ? (
              <span css={dateStyles}>{start.weekdayShortDate}</span>
            ) : null}
            {start.time ? <span css={dateStyles}>{start.time}</span> : null}
            {start.zonesDiffer && start.eventTime ? (
              <span css={secondaryTimeStyles}>{start.eventTime}</span>
            ) : null}
          </span>
        </div>
      </div>
    </Link>
  )
}

export default UpcomingEventCard

/* Dark fill, deliberately: the sidebar's other cards are light, so the
   contrast is what marks an event as a different kind of thing at a glance —
   the same reasoning the mobile card uses. */
const cardStyles = css`
  display: block;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  padding: 12px 16px;
  /* Sized to the TALLEST possible card, so every card in the carousel renders
     at the same height and a sparse one pads out to match rather than sitting
     visibly shorter — the same reasoning as the mobile card's own floor.

     The left column sets the ceiling: team 21 + title 26 + competition 15 +
     description 34 (two lines, clamped) + location 15, plus 4 gaps of 4 and
     the card's 24 of vertical padding = 151. The right column tops out lower
     (tag 18 + date 22 + time 22 + zone 20 = 82 + 24 = 106). */
  min-height: 151px;
  text-decoration: none;
  transition: opacity 0.15s ease;
  /* Pairs with the draggable={false} prop: that stops the link-drag, this
     stops the text/image selection a drag across the card would otherwise
     leave highlighted behind the swipe. */
  user-select: none;
  -webkit-user-drag: none;

  &:hover {
    opacity: 0.9;
    text-decoration: none;
  }
`

const columnsStyles = css`
  display: flex;
  gap: 12px;
  min-height: 127px;
`

const leftColumnStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const teamRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

/* 20px, pairing with the 17/21 team name — the same crest-to-name ratio the
   mobile card uses. `contain` so a non-square crest is never cropped. */
const teamLogoStyles = css`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  object-fit: contain;
  flex-shrink: 0;
`

/* Same condensed heading face as the title below it, matching the mobile
   card's TITLE_DEMI treatment — muted and a step down in weight, so it leads
   the column without competing with the event's own name. */
const teamNameStyles = css`
  ${font.heading}
  font-size: 17px;
  line-height: 21px;
  color: ${colors.NEUTRAL_30};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const titleRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

/* An antd icon rather than a text glyph — the same `CalendarOutlined` the rest
   of the web app uses for a date, so the mark is consistent across surfaces.

   Sized to the title's cap height and PRIMARY-coloured (tenant-aware for free:
   amber on SportNinja, blue on Canlan). antd icons render an SVG with
   `fill: currentColor`, so setting `color` here is what tints it. */
const typeIconStyles = css`
  font-size: 18px;
  color: ${colors.PRIMARY};
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`

const typeIconMutedStyles = css`
  color: ${colors.NEUTRAL_50};
`

/* `font.heading`, NOT `font.title`: on Canlan `title` forces uppercase and 2px
   tracking, which shouts an event name and loses the capitalisation the name
   was written with. `heading` is the same condensed face without that — and on
   SportNinja it is BarlowCondensed, matching the mobile card's TITLE_BOLD. */
const titleStyles = css`
  ${font.heading}
  ${weightSemiBold}
  font-size: 21px;
  line-height: 26px;
  color: ${colors.WHITE};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const titleCancelledStyles = css`
  text-decoration: line-through;
  color: ${colors.NEUTRAL_50};
`

const mutedStyles = css`
  color: ${colors.NEUTRAL_50};
`

/* Same treatment the game card gives its own season line, so a mixed feed
   labels its competition identically on both card types. */
const competitionStyles = css`
  ${font.body}
  font-size: 11px;
  line-height: 15px;
  color: ${colors.NEUTRAL_40};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* Mirrors the mobile card's description — 12/17, NEUTRAL_30 — capped at two
   lines. `-webkit-line-clamp` is the CSS equivalent of RN's numberOfLines={2};
   it needs the box/orient pair and `overflow: hidden` to take effect. */
const descriptionStyles = css`
  ${font.body}
  font-size: 12px;
  line-height: 17px;
  color: ${colors.NEUTRAL_30};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* `margin-top: auto` pins the venue to the bottom of the column — matching the
   mobile card, where the location always closes the card. */
const locationStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin-top: auto;
`

/* The same map-marker the schedule row uses — its path is Material Design's
   `place` glyph, i.e. the exact icon the mobile card renders, so the location
   line reads identically on both platforms. The wrapper span is the flex
   child, so it carries the shrink guard. */
const pinStyles = css`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`

const locationTextStyles = css`
  ${font.body}
  font-size: 11px;
  line-height: 15px;
  color: ${colors.NEUTRAL_20};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const rightColumnStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  max-width: 45%;
  flex-shrink: 0;
`

const tagSlotStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 18px;
`

/* Takes the height the badge leaves and centres in it, so the date sits in the
   vertical middle of the column however tall the left column grows. */
const timeGroupStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`

/* Shared by the date and the time lines, which mobile also renders at one
   size — the pair reads as a single block, and the zone line below is the only
   step down. Scaled to the 336px sidebar rather than mobile's 20px. */
const dateStyles = css`
  ${font.heading}
  ${weightSemiBold}
  font-size: 18px;
  line-height: 22px;
  color: ${colors.WHITE};
`

/* The event's OWN zone, under the viewer's when the two differ. Smaller and
   dimmer so it reads as a clarification, but the SAME face so the two still
   read as one pair. */
const secondaryTimeStyles = css`
  ${font.heading}
  font-size: 16px;
  line-height: 20px;
  color: ${colors.NEUTRAL_40};
`

const categoryTagStyles = css`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 4px;
  ${font.body}
  ${weightSemiBold}
  font-size: 9px;
  line-height: 13px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
`

/* Outlined rather than filled, matching the list row: a solid red block would
   out-shout the rest of the card, and cancelled is a modifier on the event. */
const cancelledTagStyles = css`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid ${colors.ERROR_200};
  ${font.body}
  ${weightSemiBold}
  font-size: 9px;
  line-height: 13px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.ERROR_200};
`
